/**
 * What happens when the session has actually expired.
 *
 * The access cookie lasts 15 minutes. When it lapses, the network layer tries a
 * silent refresh and retries the request — and when THAT fails (the refresh token
 * has been rotated, or the server restarted and lost its in-memory grace window),
 * core simply throws an ApiError like any other. Nothing signed the player out.
 *
 * The result was the "Rest is unauthorized" report: the game carried on looking
 * logged in, every action toasted the raw word "Unauthorized", and nothing told the
 * player what had happened or offered a way back. An expired session that presents
 * as a broken button is the worst of both — it reads as the game being broken.
 *
 * This lives in GAME code, not core: core owns the axios client and does not export
 * it, and editing core would conflict on every template merge. So the terminal 401
 * is handled where the game funnels its own errors.
 */

import { ApiError, api } from '../core/network/api';
import { authStore } from '../core/state/auth-store';
import { toast } from './dom';

/** Guards against a burst of failing requests each firing their own redirect. */
let handling = false;

/**
 * True when this error means "you are signed out", and the sign-out has been
 * started. Callers should treat a `true` return as handled and NOT toast their own
 * message on top of it.
 *
 * A 401 reaching here is already terminal: core retried once behind a refresh, so
 * by the time it surfaces the refresh has failed too.
 */
export function handleSessionExpiry(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.status !== 401) return false;
  if (handling) return true;
  handling = true;

  // Core's interceptor may have already called endSession() (clearing authStore
  // and disconnecting the socket) before the ApiError reached game code. If so,
  // the session is confirmed dead — skip the /auth/me probe and go straight to
  // sign-out. Without this check both paths race: endSession's authStore
  // subscriber navigates while signOut's setTimeout navigates 1.2s later.
  if (authStore.get().user === null) {
    signOut();
    return true;
  }

  // VERIFY before evicting.
  //
  // A 401 arriving here is not proof the session is dead. Core's interceptor
  // refreshes and retries once; if the RETRY also 401s it throws, and everything
  // downstream treated that as "you are signed out" — so any single endpoint that
  // answers 401 for a reason of its own would throw the player out of a game they
  // were in the middle of, with nothing but the word "Unauthorized" to go on.
  //
  // /auth/me settles it. Alive = one endpoint's refusal, so toast and let the
  // player retry; dead = sign out. The probe runs behind the `handling` latch,
  // so a burst of failures still makes exactly one.
  const path = ((e.details as { path?: string } | undefined)?.path) ?? 'an endpoint';
  void api.get('/auth/me')
    .then(() => {
      // eslint-disable-next-line no-console
      console.warn(`[auth] 401 from ${path} — the session is still valid, staying signed in`);
      toast('That action was refused — please try again', 'error');
      handling = false;
    })
    .catch(() => {
      // eslint-disable-next-line no-console
      console.warn(`[auth] 401 from ${path} — the session is gone, signing out`);
      signOut();
    });
  return true;
}

/** The session really is over: clear it and get the player back to a sign-in. */
function signOut(): void {
  // Only clear and toast if the store still has a user — endSession() in api.ts may
  // have already nulled it, and re-setting + re-toasting would fight the router.
  if (authStore.get().user !== null) {
    authStore.set({ user: null, status: 'idle', error: null });
    toast('Your session expired — please sign in again', 'error');
  }

  setTimeout(() => {
    handling = false;
    window.location.assign('/login');
  }, 1200);
}

/** Test seam: forget an in-flight sign-out decision. */
export function __resetSessionExpiry(): void { handling = false; }
